import { darkenHex, hexToRgb, mixHex, normalizeHex } from './colorUtils'

export type DerivedMuahangPalette = {
  primaryMain: string
  primaryDark: string
  primaryLight: string
  headerBg: string
  navItemActiveBg: string
  navItemActiveBgHover: string
  kiotSubmenuPanelBg: string
  invoiceTableHeaderBg: string
  tableLevel2HeadBg: string
  tableLevel2HeadBorder: string
  tableHeadBg: string
  tableHeadBorder: string
  /** Viền ngoài khối bảng (list). */
  tableWrapperBorder: string
  /** Kẻ ô body (ngang/dọc). */
  tableCellBorder: string
  rowHoverBg: string
  rowSelectedBg: string
  rowSelectedHoverBg: string
  primarySurface: string
  primarySurfaceBorder: string
  primaryHoverRgba: string
}

/** Độ sáng tương đối 0..1 — dùng chỉnh tint bảng cho accent tối (navy/slate). */
function relativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex)
  if (!rgb) return 0.5
  const toLin = (c: number) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * toLin(rgb.r) + 0.7152 * toLin(rgb.g) + 0.0722 * toLin(rgb.b)
}

export function deriveMuahangFromMain(mainRaw: string): DerivedMuahangPalette {
  const main = normalizeHex(mainRaw)
  const lum = relativeLuminance(main)
  /** Accent tối (navy/slate): giảm % trắng để tint còn nhìn thấy, hover rõ hơn. */
  const darkAccent = lum < 0.28

  const primaryDark = darkenHex(main, 0.14)
  const primaryLight = mixHex(main, '#ffffff', 0.38)
  /** Khớp KiotViet `.kv-nav-link.active` (~darken 19% từ `#0070f4` → `#005ac3`). */
  const navItemActiveBg = darkenHex(main, 0.19)
  const navItemActiveBgHover = darkenHex(main, 0.26)
  const kiotSubmenuPanelBg = navItemActiveBg

  const tableHeadBg = mixHex(main, '#f7f8fa', darkAccent ? 0.82 : 0.9)
  /** Viền thead — xám trung tính, pha accent rất nhẹ (tránh lưới màu gắt). */
  const tableHeadBorder = mixHex(main, '#e4e7ec', darkAccent ? 0.82 : 0.9)
  /** Viền khối bảng — xám trung tính. */
  const tableWrapperBorder = mixHex(main, '#e8eaed', 0.94)
  /** Kẻ ô body — nhạt hơn viền thead. */
  const tableCellBorder = mixHex(main, '#f0f1f3', 0.94)
  const rowHoverBg = mixHex(main, '#ffffff', darkAccent ? 0.94 : 0.96)
  const rowSelectedBg = mixHex(main, '#ffffff', darkAccent ? 0.86 : 0.9)
  const rowSelectedHoverBg = mixHex(main, '#ffffff', darkAccent ? 0.8 : 0.84)
  const invoiceTableHeaderBg = tableHeadBg
  /** Bảng level 2 — nền header nhạt hơn level 1. */
  const tableLevel2HeadBg = mixHex(main, '#fafbfc', darkAccent ? 0.88 : 0.94)
  const tableLevel2HeadBorder = mixHex(main, '#e8eaed', darkAccent ? 0.86 : 0.92)
  const primarySurface = mixHex(main, '#ffffff', 0.92)
  const primarySurfaceBorder = mixHex(main, '#d0d0d0', 0.35)
  const px = hexToRgb(main)
  const primaryHoverRgba = px ? `rgba(${px.r}, ${px.g}, ${px.b}, 0.08)` : 'rgba(0, 112, 244, 0.08)'

  return {
    primaryMain: main,
    primaryDark,
    primaryLight,
    headerBg: main,
    navItemActiveBg,
    navItemActiveBgHover,
    kiotSubmenuPanelBg,
    invoiceTableHeaderBg,
    tableLevel2HeadBg,
    tableLevel2HeadBorder,
    tableHeadBg,
    tableHeadBorder,
    tableWrapperBorder,
    tableCellBorder,
    rowHoverBg,
    rowSelectedBg,
    rowSelectedHoverBg,
    primarySurface,
    primarySurfaceBorder,
    primaryHoverRgba,
  }
}
